import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X, ChevronLeft, ChevronRight, Heart, Share2, Play, Pause,
  Download, Trash2, CheckCircle2, ImageOff, Loader2, AlertCircle,
} from "lucide-react";
import { logger } from "@/lib/logger";


// iter44 — Module-level animation constants avoid creating new object refs on
// every render (React.memo optimisation + reduces framer-motion churn).
const LB_SLIDE_TRANSITION = { x: { type: "spring", stiffness: 320, damping: 34 }, opacity: { duration: 0.18 } };
const LB_FADE_INITIAL     = { opacity: 0 };
const LB_FADE_ANIMATE     = { opacity: 1 };
const LB_FADE_EXIT        = { opacity: 0 };
const LB_CAPTION_INITIAL  = { y: 20, opacity: 0 };
const LB_CAPTION_ANIMATE  = { y: 0, opacity: 1 };
const LB_TOOLBAR_INITIAL  = { y: 12, opacity: 0, scale: 0.98 };
const LB_TOOLBAR_ANIMATE  = { y: 0, opacity: 1, scale: 1 };
const LB_TOOLBAR_EXIT     = { y: 6, opacity: 0 };

/**
 * Premium client-portal lightbox.
 *
 * Design goals:
 *   - Zero layout jump between slides (fixed-size stage, object-contain)
 *   - 60fps swipes on touch, arrow-key nav on desktop
 *   - Adjacent-image preload for instant next/prev
 *   - Prop-driven "delete/remove" — guests remove-from-selection, admins hard-delete
 *   - Dark backdrop (near-black) so the photo owns the frame
 *
 * The component is completely presentation-layer — it never talks to the API
 * directly. Callers pass `onDeleteSelected(ids)` to wire the real work, and
 * `onToggleSelect(id)` to persist favourites wherever they want (localStorage,
 * server, redux, etc).
 */
export default function Lightbox({
  photos,
  startIndex = 0,
  onClose,
  getImageUrl,
  getDownloadUrl,           // iter198: optional explicit URL for the download button (routes through the watermark-aware endpoint)
  getFilename = (p) => p.filename || "photo.jpg",
  selectedIds,               // Set<string>
  onToggleSelect,            // (id) => void
  onDeleteSelected,          // (ids: string[]) => Promise<void> | void  (optional)
  deleteLabel = "Remove from selection",
  deleteVerb = "remove",     // used in the confirmation modal copy
  destructive = false,       // true = red / "delete permanently" wording
  showShare = true,
  shareUrl = "",
  showDownload = true,
  showSelection = true,      // hide the whole favourites bar (admin flows)
  slideshowIntervalMs = 3500,
}) {
  const total = photos.length;
  const [idx, setIdx] = useState(Math.max(0, Math.min(startIndex, total - 1)));
  const [dir, setDir] = useState(0);       // -1 back / +1 forward — powers slide direction
  const [slideshow, setSlideshow] = useState(false);
  const [confirm, setConfirm] = useState(null); // { ids, title, body }
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const touch = useRef({ x0: 0, y0: 0, t0: 0, active: false });

  const current = photos[idx];

  const go = useCallback(
    (delta) => {
      if (!total) return;
      setDir(delta > 0 ? 1 : -1);
      setImgLoaded(false);
      setIdx((prev) => (prev + delta + total) % total);
    },
    [total],
  );

  const goTo = useCallback(
    (i) => {
      if (!total) return;
      if (i === idx) return;
      setDir(i > idx ? 1 : -1);
      setImgLoaded(false);
      setIdx(((i % total) + total) % total);
    },
    [idx, total],
  );

  // ----- Keyboard -----
  useEffect(() => {
    const onKey = (e) => {
      if (confirm) return; // freeze nav while the modal is open
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key === "ArrowRight") { go(1); return; }
      if (e.key === "ArrowLeft") { go(-1); return; }
      if (e.key === " " || e.key === "Spacebar") { e.preventDefault(); setSlideshow((s) => !s); return; }
      if (e.key === "f" || e.key === "F") {
        if (showSelection && current) onToggleSelect?.(current.id);
        return;
      }
      if (e.key === "d" || e.key === "D") {
        if (showDownload && current) triggerDownload(current);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose, current, onToggleSelect, showSelection, showDownload, confirm]);

  // ----- Auto-advance for slideshow -----
  useEffect(() => {
    if (!slideshow || !total) return undefined;
    const h = setTimeout(() => go(1), slideshowIntervalMs);
    return () => clearTimeout(h);
  }, [slideshow, idx, go, slideshowIntervalMs, total]);

  // ----- Adjacent preloading (previous + next only) -----
  useEffect(() => {
    if (!total) return;
    const preload = (i) => {
      const p = photos[((i % total) + total) % total];
      if (!p) return;
      const img = new Image();
      img.src = getImageUrl(p);
    };
    preload(idx + 1);
    preload(idx - 1);
  }, [idx, photos, getImageUrl, total]);

  // ----- Touch swipe -----
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x0: t.clientX, y0: t.clientY, t0: Date.now(), active: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x0;
    const dy = t.clientY - touch.current.y0;
    const dt = Date.now() - touch.current.t0;
    touch.current.active = false;
    if (Math.abs(dx) < 45) return;                       // ignore tiny jitters
    if (Math.abs(dy) > Math.abs(dx) * 0.9) return;       // ignore vertical scrolls
    if (dt > 800) return;                                // ignore super-slow drags
    go(dx < 0 ? 1 : -1);
  };

  // ----- Download -----
  const triggerDownload = async (photo) => {
    if (!photo) return;
    try {
      // iter198: prefer the watermark-aware download URL when provided so the
      // downloaded file always matches what the client saw in the viewer.
      const url = (typeof getDownloadUrl === "function" && getDownloadUrl(photo)) || getImageUrl(photo);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFilename(photo);
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // Browsers usually still fire the download for cross-origin URLs even
      // when this throws — log for diagnostics but don't surface to the user.
      logger.warn("download link failed:", e?.message);
    }
  };

  // ----- Share -----
  const share = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl, title: "Wedding album" });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // User cancelled the share sheet or clipboard write was denied — both
      // are expected and non-fatal, but we log for diagnostics.
      logger.debug("share/copy cancelled:", e?.message);
    }
  };

  // ----- Delete / Remove selected -----
  const selectedCount = selectedIds ? selectedIds.size : 0;
  const isSelectedCurrent = !!(selectedIds && current && selectedIds.has(current.id));

  const askDeleteSelected = () => {
    if (!selectedCount) return;
    setConfirm({
      ids: Array.from(selectedIds),
      title: destructive ? "Delete selected photos?" : "Remove from your selection?",
      body: destructive
        ? `You are about to permanently delete ${selectedCount} photo${selectedCount === 1 ? "" : "s"}. This cannot be undone.`
        : `Clear ${selectedCount} photo${selectedCount === 1 ? "" : "s"} from your favourites list?`,
    });
  };

  const askDeleteCurrent = () => {
    if (!current) return;
    setConfirm({
      ids: [current.id],
      title: destructive ? "Delete this photo?" : "Remove from your selection?",
      body: destructive
        ? "This photo will be permanently deleted from the album."
        : "This photo will be cleared from your favourites list.",
    });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (onDeleteSelected) {
        await onDeleteSelected(confirm.ids);
      } else if (onToggleSelect) {
        // Default (no server call) → just clear from local selection
        confirm.ids.forEach((id) => onToggleSelect(id));
      }
      setConfirm(null);
    } catch (e) {
      setConfirm((c) => c && { ...c, error: e?.message || "Something went wrong." });
    } finally {
      setBusy(false);
    }
  };

  if (!total || !current) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center text-white/70 text-sm" onClick={onClose} data-testid="lightbox-empty">
        <ImageOff className="w-6 h-6 mr-2" strokeWidth={1.4} /> No photos to display
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0b0b0f] text-white select-none"
      data-testid="lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* ============ Top bar ============ */}
      <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 sm:px-6 h-14 bg-gradient-to-b from-black/70 to-transparent">
        <div className="text-xs sm:text-sm font-mono tracking-wide text-white/80" data-testid="lightbox-counter">
          <span className="text-white">{idx + 1}</span>
          <span className="text-white/40"> / {total}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {showSelection && (
            <ToolbarButton
              testId="lightbox-toggle-favorite"
              label={isSelectedCurrent ? "Remove favourite" : "Favourite"}
              onClick={() => onToggleSelect?.(current.id)}
              active={isSelectedCurrent}
              activeClass="text-rose-500"
            >
              <Heart className="w-4 h-4" strokeWidth={2} fill={isSelectedCurrent ? "currentColor" : "none"} />
            </ToolbarButton>
          )}
          {showShare && shareUrl && (
            <ToolbarButton testId="lightbox-share" label={copied ? "Link copied" : "Share album"} onClick={share}>
              {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" strokeWidth={1.8} />}
            </ToolbarButton>
          )}
          <ToolbarButton
            testId="lightbox-toggle-slideshow"
            label={slideshow ? "Pause slideshow" : "Play slideshow"}
            onClick={() => setSlideshow((s) => !s)}
            active={slideshow}
            activeClass="text-amber-300"
          >
            {slideshow ? <Pause className="w-4 h-4" strokeWidth={1.8} /> : <Play className="w-4 h-4" strokeWidth={1.8} />}
          </ToolbarButton>
          {showDownload && (
            <ToolbarButton testId="lightbox-download" label="Download" onClick={() => triggerDownload(current)}>
              <Download className="w-4 h-4" strokeWidth={1.8} />
            </ToolbarButton>
          )}
          {(destructive || (showSelection && isSelectedCurrent)) && (
            <ToolbarButton
              testId="lightbox-remove-current"
              label={destructive ? "Delete photo" : "Remove from selection"}
              onClick={askDeleteCurrent}
              danger={destructive}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.8} />
            </ToolbarButton>
          )}
          <div className="w-px h-6 bg-white/10 mx-1" />
          <ToolbarButton testId="lightbox-close" label="Close" onClick={onClose}>
            <X className="w-5 h-5" strokeWidth={1.8} />
          </ToolbarButton>
        </div>
      </div>

      {/* ============ Stage ============ */}
      <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-16">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={total <= 1}
          aria-label="Previous"
          data-testid="lightbox-prev"
          className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed items-center justify-center transition"
        >
          <ChevronLeft className="w-6 h-6" strokeWidth={1.6} />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={total <= 1}
          aria-label="Next"
          data-testid="lightbox-next"
          className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed items-center justify-center transition"
        >
          <ChevronRight className="w-6 h-6" strokeWidth={1.6} />
        </button>

        {/* Sliding image */}
        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
          <AnimatePresence initial={false} mode="popLayout" custom={dir}>
            <motion.img
              key={current.id}
              src={getImageUrl(current)}
              alt={getFilename(current)}
              onLoad={() => setImgLoaded(true)}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={LB_SLIDE_TRANSITION}
              draggable={false}
              className="max-w-full max-h-[85vh] object-contain will-change-transform"
              data-testid="lightbox-image"
            />
          </AnimatePresence>
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* ============ Bottom batch bar ============ */}
      {showSelection && selectedCount > 0 && (
        <div className="absolute inset-x-0 bottom-0 z-30 px-4 sm:px-6 pb-4 sm:pb-6 pointer-events-none">
          <motion.div
            initial={LB_CAPTION_INITIAL}
            animate={LB_CAPTION_ANIMATE}
            className="pointer-events-auto mx-auto max-w-2xl flex items-center gap-3 bg-white/8 backdrop-blur-md border border-white/10 rounded-full px-4 py-2.5 text-sm shadow-2xl shadow-black/40"
            data-testid="lightbox-batch-bar"
          >
            <Heart className="w-4 h-4 text-rose-400 fill-rose-500" strokeWidth={0} />
            <span className="font-semibold" data-testid="lightbox-batch-count">
              {selectedCount} favourite{selectedCount === 1 ? "" : "s"}
            </span>
            <span className="hidden sm:inline text-white/40">·</span>
            <span className="hidden sm:inline text-xs text-white/60">Press F to favourite · Space for slideshow</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={askDeleteSelected}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${
                  destructive
                    ? "bg-red-500/90 hover:bg-red-500 text-white"
                    : "bg-white/10 hover:bg-white/20 text-white"
                }`}
                data-testid="lightbox-batch-delete"
              >
                <Trash2 className="w-3.5 h-3.5 inline-block mr-1" strokeWidth={1.8} />
                {deleteLabel}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ============ Confirm modal ============ */}
      <AnimatePresence>
        {confirm && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-4"
            initial={LB_FADE_INITIAL} animate={LB_FADE_ANIMATE} exit={LB_FADE_EXIT}
            onClick={() => !busy && setConfirm(null)}
            data-testid="lightbox-confirm"
          >
            <motion.div
              initial={LB_TOOLBAR_INITIAL}
              animate={LB_TOOLBAR_ANIMATE}
              exit={LB_TOOLBAR_EXIT}
              className="bg-stone-900 border border-white/10 rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 ${destructive ? "bg-red-500/15 text-red-400" : "bg-rose-500/15 text-rose-400"}`}>
                <AlertCircle className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <h3 className="text-lg font-semibold text-white">{confirm.title}</h3>
              <p className="text-sm text-white/60 mt-1.5">{confirm.body}</p>
              {confirm.error && (
                <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                  {confirm.error}
                </div>
              )}
              <div className="mt-5 flex justify-end gap-2">
                <button
                  className="text-sm px-4 py-2 rounded-lg text-white/70 hover:text-white transition"
                  onClick={() => setConfirm(null)}
                  disabled={busy}
                  data-testid="lightbox-confirm-cancel"
                >
                  Cancel
                </button>
                <button
                  className={`text-sm px-4 py-2 rounded-lg font-semibold text-white transition inline-flex items-center gap-2 ${destructive ? "bg-red-600 hover:bg-red-700" : "bg-rose-600 hover:bg-rose-700"}`}
                  onClick={runConfirmed}
                  disabled={busy}
                  data-testid="lightbox-confirm-ok"
                >
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {destructive ? "Delete" : `Yes, ${deleteVerb}`}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ------------------------------------------------------------------
// Tiny presentational bits
// ------------------------------------------------------------------
function ToolbarButton({ children, label, onClick, active, activeClass = "", danger, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
        danger
          ? "text-red-300 hover:bg-red-500/15"
          : active
          ? `${activeClass} hover:bg-white/10`
          : "text-white/70 hover:text-white hover:bg-white/10"
      }`}
    >
      {children}
    </button>
  );
}

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
};

// ------------------------------------------------------------------
// A small hook so callers can persist favourites to localStorage per-event
// without every page re-implementing the plumbing.
// ------------------------------------------------------------------
export function useFavourites(storageKey) {
  const readInitial = () => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(storageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  };
  const [ids, setIds] = useState(readInitial);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(Array.from(ids)));
    } catch (e) {
      // Quota exceeded / storage disabled (Safari private mode) — favourites
      // just don't survive a reload. Non-fatal.
      logger.warn("favourites persist failed:", e?.message);
    }
  }, [ids, storageKey]);

  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback((idsToClear) => {
    setIds((prev) => {
      const next = new Set(prev);
      (idsToClear || []).forEach((i) => next.delete(i));
      return next;
    });
  }, []);

  const setAll = useCallback((all) => setIds(new Set(all || [])), []);

  return useMemo(() => ({ ids, toggle, clear, setAll }), [ids, toggle, clear, setAll]);
}
